import { HotelsAvailabilityActions } from "@b2bportal/core-hotels";
import {
  BundleResponseEnum,
  PackageEndpointsV2,
  type PackagesCustomizeLodgingAvailabilityMoreRequest,
  type PackagesCustomizeLodgingAvailabilityMoreResponse,
} from "@b2bportal/core-packages";
import { axiosInstance } from "@hopper-b2b/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { PackageHotelActions } from "../../slice/packagesHotels";

// Module-level controller shared across all recursion levels.
// Calling abortCurrentHotelAvailabilityMore() stops the entire chain,
// including any in-flight HTTP requests.
let _abortController = new AbortController();

export const abortCurrentHotelAvailabilityMore = () => {
  _abortController.abort();
  _abortController = new AbortController();
};

export const fetchPackageHotelAvailabilityMore = createAsyncThunk<
  void,
  { nextPageToken: string; searchId: string; packageStateToken: string }
>(
  "packages/fetchMoreHotels",
  async ({ nextPageToken, searchId, packageStateToken }, thunkAPI) => {
    // Snapshot the signal before any await. abortCurrentHotelAvailabilityMore()
    // replaces the module-level controller, so checking _abortController.signal
    // after an await would always see a fresh (non-aborted) instance.
    const signal = _abortController.signal;
    if (signal.aborted) return;

    try {
      thunkAPI.dispatch(HotelsAvailabilityActions.handleFetchNextPagePending());

      const nextPageRequest: PackagesCustomizeLodgingAvailabilityMoreRequest = {
        customize: {
          bundledPackageStateToken: { value: packageStateToken },
          lodgingAvailabilityRequest: {
            moreToken: nextPageToken,
            LodgingAvailabilityRequest: "FollowUpRequest",
          },
        },
      };

      const {
        data: nextPageResponse,
      }: { data: PackagesCustomizeLodgingAvailabilityMoreResponse } =
        await axiosInstance.post(
          PackageEndpointsV2.Customize,
          nextPageRequest,
          { signal }
        );

      if (
        nextPageResponse.BundleResponse ===
        BundleResponseEnum.LodgingAvailability
      ) {
        // Gate: if the user navigated away from packages while the /customize
        // request was in flight, skip state updates and further pagination.
        if (signal.aborted) return;

        thunkAPI.dispatch(
          PackageHotelActions.setPackageStateByLodgingId({
            ...nextPageResponse.lodgingAvailability.packageStateByLodgingId,
          })
        );

        const nextPageHotelResponse =
          nextPageResponse.lodgingAvailability.payload.availability;
        thunkAPI.dispatch(
          HotelsAvailabilityActions.handleFetchNextPageFulfilled({
            ...nextPageHotelResponse,
            searchId,
          })
        );

        if (nextPageHotelResponse.nextPageToken != null && !signal.aborted) {
          const newNextPageToken = nextPageHotelResponse.nextPageToken;

          await thunkAPI.dispatch(
            fetchPackageHotelAvailabilityMore({
              nextPageToken: newNextPageToken,
              searchId,
              packageStateToken,
            })
          );
        }
      }
    } catch (e) {
      if (signal.aborted) return; // clean navigation-away, not a real failure

      console.error("Failed to fetch more hotels:", e);
      const errorMessage = e instanceof Error ? e.message : String(e);
      const error = { error: errorMessage };
      thunkAPI.dispatch(
        HotelsAvailabilityActions.handleFetchNextPageRejected(error)
      );
      return thunkAPI.rejectWithValue(error);
    }
  }
);
